import { EditorState } from "draft-js";
import { Dictionary } from "../../../../entities/common";
import { EditorUtils } from "./EditorUtils";
import { colorStyleMap, highlightStyleMap } from "./common";

export type HotKey = {
    macos: {
        option?: boolean,
        cmd?: boolean,
    },
    win: {
        ctrl?: boolean,
        alt?: boolean,
    },
    key: string,
    keyCode?: number,
}

export type RichtextEditorCommand = {
    name: string,
    iconName?: string,
    description?: string,
    style?: string,
    hotKey?: HotKey,
    onCommand: (editorState: EditorState, data?: any) => EditorState,
}

export const RichtextEditorCommands: Dictionary<RichtextEditorCommand> = {
    'bold': {
        name: 'Bold',
        iconName: 'Bold',
        style: 'BOLD',
        hotKey: { key: 'b', keyCode: 66, win: { ctrl: true }, macos: { cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleInlineStyles(editorState, ["BOLD"])
    },
    'underline': {
        name: 'Underline',
        iconName: 'Underline',
        style: 'UNDERLINE',
        hotKey: { key: 'u', keyCode: 85, win: { ctrl: true }, macos: { cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleInlineStyles(editorState, ["UNDERLINE"])
    },
    'italic': {
        name: 'Italic',
        iconName: 'Italic',
        style: 'ITALIC',
        hotKey: { key: 'i', keyCode: 73, win: { ctrl: true }, macos: { cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleInlineStyles(editorState, ["ITALIC"])
    },
    'strikethrough': {
        name: 'Strikethrough',
        iconName: 'Strikethrough',
        style: 'STRIKETHROUGH',
        hotKey: { key: 's', keyCode: 83, win: { alt: true, ctrl: true }, macos: { option: true, cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleInlineStyles(editorState, ["STRIKETHROUGH"])
    },

    'header-one': {
        name: 'Header 1',
        iconName: 'Header1',
        style: 'header-one',
        hotKey: { key: '1', keyCode: 49, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "header-one")
    },
    'header-two': {
        name: 'Header 2',
        iconName: 'Header2',
        style: 'header-two',
        hotKey: { key: '2', keyCode: 50, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "header-two")
    },
    'header-three': {
        name: 'Header 3',
        iconName: 'Header3',
        style: 'header-three',
        hotKey: { key: '3', keyCode: 51, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "header-three")
    },
    'header-four': {
        name: 'Header 4',
        iconName: 'Header4',
        style: 'header-four',
        hotKey: { key: '4', keyCode: 52, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "header-four")
    },
    'unstyled': {
        name: 'Paragraph',
        iconName: 'FontSize',
        style: 'unstyled',
        hotKey: { key: '0', keyCode: 48, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "unstyled")
    },

    'ul': {
        name: 'Bulleted list',
        iconName: 'BulletedList',
        style: 'unordered-list-item',
        hotKey: { key: '.', keyCode: 190, win: { ctrl: true }, macos: { cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "unordered-list-item")
    },
    'ol': {
        name: 'Numbered list',
        iconName: 'NumberedList',
        style: 'ordered-list-item',
        hotKey: { key: '/', keyCode: 191, win: { ctrl: true }, macos: { cmd: true } },
        onCommand: (editorState: EditorState): EditorState => EditorUtils.toggleBlockType(editorState, "ordered-list-item")
    },

    'clear': {
        name: 'Clear format',
        iconName: 'ClearFormatting',
        hotKey: { key: '\\', keyCode: 220, win: { ctrl: true }, macos: { cmd: true } },
        style: 'clear-format',
        onCommand: (editorState: EditorState): EditorState => EditorUtils.clearFormat(editorState),
    },

    'color': {
        name: 'Font color',
        iconName: "FontColor",
        onCommand: (editorState: EditorState, data?: any): EditorState => {
            return EditorUtils.toggleColorStyle(editorState, data, Object.keys(colorStyleMap));
        }
    },

    'highlight': {
        name: 'Highlight',
        iconName: "FabricTextHighlightComposite",
        onCommand: (editorState: EditorState, data?: any): EditorState => {
            return EditorUtils.toggleColorStyle(editorState, data, Object.keys(highlightStyleMap));
        }
    },

    'link': {
        name: 'Insert a hyperlink',
        iconName: 'Link',
        style: 'create-link',
        hotKey: { key: 'k', keyCode: 75, win: { ctrl: true, alt: true }, macos: { cmd: true, option: true } },
        onCommand: (editorState: EditorState, data?: any): EditorState => EditorUtils.link(editorState, data)
    }
}
